import { useState } from "react";
import { Loader2, Check, X } from "lucide-react";
import { api, USER_ID } from "../api";
import { toast } from "sonner";
import { FoodRec } from "./FoodRec";
import { badgeToast, melaToast, MelaCoin } from "./BadgesSheet";

const HOWTO_KEY = "xp_howto_masala";

export const MasalaMatch = ({ refresh }) => {
  const [howto, setHowto] = useState(() => localStorage.getItem(HOWTO_KEY) !== "1");
  const [round, setRound] = useState(null);
  const [selSpice, setSelSpice] = useState(null);
  const [matches, setMatches] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    setResult(null);
    setMatches({});
    setSelSpice(null);
    try {
      const r = await api.post("/arcade/masala/start", { user_id: USER_ID });
      setRound(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    setBusy(false);
  };

  const pickDish = (dish) => {
    if (!selSpice || Object.values(matches).includes(dish)) return;
    setMatches((m) => ({ ...m, [selSpice]: dish }));
    setSelSpice(null);
  };

  const unmatch = (spice) => setMatches((m) => { const n = { ...m }; delete n[spice]; return n; });

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/arcade/masala/submit", { user_id: USER_ID, session_id: round.session_id, matches });
      setResult(r.data);
      if (r.data.mela_result?.awarded) melaToast(r.data.mela_result.message);
      badgeToast(r.data.new_badges);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };

  const allMatched = round && Object.keys(matches).length === round.spices.length;

  return (
    <div data-testid="game-masala-match" className="rounded-2xl bg-[#121212] border border-[#E25822]/30 p-5">
      <p className="text-[10px] tracking-[0.3em] text-[#E25822] font-bold">MASALA MATCH · ਮਸਾਲਾ ਮਿਲਾਨ</p>
      <p className="text-xs text-white/50 mt-1">Match each spice to the dish it makes famous. Regal Coins for every correct pair.</p>

      {howto && (
        <div data-testid="masala-howto" className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#1A1208] p-4">
          <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-black">HOW TO PLAY</p>
          <p className="text-xs text-white/70 mt-1.5 leading-relaxed">Tap a spice on the left, then tap the dish it belongs to on the right. Match all six, then submit. Tap a pair to undo it.</p>
          <button data-testid="masala-howto-ok" onClick={() => { localStorage.setItem(HOWTO_KEY, "1"); setHowto(false); }}
            className="mt-3 h-9 px-5 rounded-full bg-[#D4AF37] text-black text-[10px] font-black tracking-widest">GOT IT</button>
        </div>
      )}

      {!round && !howto && (
        <button data-testid="masala-start" onClick={start} disabled={busy}
          className="mt-4 w-full h-12 rounded-full bg-[#E25822] text-white font-black text-sm tracking-widest disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : "START MATCHING"}
        </button>
      )}

      {round && !result && (
        <>
          <div className="grid grid-cols-2 gap-2.5 mt-4">
            <div className="space-y-2">
              <p className="text-[9px] tracking-widest text-white/40 font-black">SPICES</p>
              {round.spices.map((s) => {
                const matched = matches[s];
                return (
                  <button key={s} data-testid={`masala-spice-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    onClick={() => (matched ? unmatch(s) : setSelSpice(selSpice === s ? null : s))}
                    className={`w-full text-left rounded-xl border px-3 py-2.5 text-xs font-semibold leading-snug ${matched ? "border-[#D4AF37]/60 bg-[#1C160A] text-[#D4AF37]" : selSpice === s ? "border-[#E25822] bg-[#E25822]/10" : "border-white/10 bg-[#1A1A1A]"}`}>
                    {s}
                    {matched && <span className="block text-[9px] text-white/45 mt-0.5">→ {matched} (tap to undo)</span>}
                  </button>
                );
              })}
            </div>
            <div className="space-y-2">
              <p className="text-[9px] tracking-widest text-white/40 font-black">DISHES</p>
              {round.dishes.map((d) => {
                const taken = Object.values(matches).includes(d);
                return (
                  <button key={d} data-testid={`masala-dish-${d.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    onClick={() => pickDish(d)} disabled={taken || !selSpice}
                    className={`w-full text-left rounded-xl border px-3 py-2.5 text-xs font-semibold leading-snug ${taken ? "border-white/5 text-white/25" : selSpice ? "border-[#D4AF37]/40 bg-[#1A1A1A] hover:bg-[#1C160A]" : "border-white/10 bg-[#1A1A1A] opacity-60"}`}>
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
          <button data-testid="masala-submit" onClick={submit} disabled={!allMatched || busy}
            className="mt-4 w-full h-12 rounded-full bg-[#D4AF37] text-black font-black text-sm tracking-widest disabled:opacity-40">
            {busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : allMatched ? "SUBMIT PAIRS" : `MATCH ALL ${round.spices.length} FIRST`}
          </button>
        </>
      )}

      {result && (
        <div className="mt-4" data-testid="masala-result">
          <p className={`font-display text-2xl font-bold ${result.perfect ? "text-[#D4AF37]" : ""}`}>
            {result.correct}/{result.total} {result.perfect ? "— SHABASH, MASALA USTAAD!" : "matched"}
          </p>
          <div className="mt-2 space-y-1">
            {result.results.map((r) => (
              <p key={r.spice} className="text-xs flex items-center gap-1.5">
                {r.correct ? <Check size={12} className="text-green-400" /> : <X size={12} className="text-[#E25822]" />}
                <span className="text-white/70">{r.spice}</span> <span className="text-white/35">→</span> <span className="font-bold">{r.dish}</span>
              </p>
            ))}
          </div>
          {result.mela_result && <p className="text-xs text-[#F0C742] font-bold mt-2 flex items-center justify-center gap-1.5"><MelaCoin size={15} /> {result.mela_result.message}</p>}
          <button data-testid="masala-again" onClick={start} className="mt-3 h-10 px-6 rounded-full border border-[#E25822]/60 text-[#E25822] text-[10px] font-black tracking-widest">PLAY AGAIN</button>
          <FoodRec source="masala" />
        </div>
      )}
    </div>
  );
};
